import { ColumnData, ColumnRegular } from '@revolist/react-datagrid';

import {
  SelectOption,
  createSelectCellTemplate,
  createSelectEditor,
  editButtonCellTemplate,
} from 'src/components/streametric/revoGridTable';
import { DataSourceTypeEnum } from 'src/types/data-sources/types';
import { GatewayOptionsEnum, GatewayOptionsTypes } from 'src/types/gateways';

import {
  resolveDataTypeOptions,
  resolveRawUnitOptions,
} from '../components/EditTagModalFormV2/editTagModalFormV2Rules';
import dataTypes from '../data/data-types.json';

/**
 * A single column of the v2 tag table. It is RevoGrid's own column type — the
 * table head, the cell renderer and the editor are one declaration there, not
 * three — so `name` is the header label and `prop` is the key it reads off the
 * row model (`GatewayInput`, flattened where the value lives under
 * `advancedTagMapping`).
 */
export type GatewayTagColumn = ColumnRegular;

/**
 * Tag Type options, from the same `data/data-types.json` v1's RHFAutocomplete
 * reads. The model stores the value (`alarm`), the user sees the label
 * (`Alarm`) — both the editor and the resting-state replica map between them.
 */
export const TAG_TYPE_OPTIONS: SelectOption[] = dataTypes;
/** Built once at module scope: `columns` has to stay a stable reference. */
const tagTypeEditor = createSelectEditor(TAG_TYPE_OPTIONS);
const tagTypeEditorForCalculated = createSelectEditor([
  { label: 'Calculated', value: 'calculated' },
]);

/**
 * Raw Unit's and Data Type (of values)'s option lists both depend on the
 * row's own Tag Type (and, for Data Type, its Allowed Inputs too) — that
 * business logic lives in exactly one place,
 * `EditTagModalFormV2/editTagModalFormV2Rules.ts`, shared by this grid's
 * dropdowns and the edit modal. This file only wires the resolvers into
 * RevoGrid's editor/cellTemplate machinery.
 */
const rawUnitEditor = createSelectEditor(resolveRawUnitOptions);
const dataTypeOfValuesEditor = createSelectEditor(resolveDataTypeOptions);

/**
 * The raw-input head — v1's `RAW_INPUT_GATEWAY_TABLE_HEAD`, which it uses for
 * every gateway that is not EWON or Kallipr. Those two are the exception (no
 * advanced-tag-mapping columns, `EWON_GATEWAY_TABLE_HEAD`), so the types below
 * share this one list rather than each declaring its own copy.
 *
 * Grouped under two subheader cells: the blank one covers just the identity
 * columns (Tag Name, Tag Type); everything writable only through Advanced Tag
 * Mapping — plus Unit and Tag Display Name, which stay directly editable —
 * sits under "Advanced". Visibility has no column of its own: it's set by
 * selecting rows and using the grid's Hide/Unhide toolbar action instead.
 */
export const RAW_INPUT_TAG_TABLE_COLUMNS: ColumnData = [
  {
    name: '',
    children: [
      { prop: 'rawInputName', name: 'Tag Name', size: 200, readonly: true },
      {
        /**
         * The dropdown column — a real MUI <Select> on edit, and an outlined-input
         * replica at rest so the column looks like the v1 field even when no cell
         * is being edited. `editor` takes an EditorCtr directly, so the grid needs
         * no `editors` map.
         *
         * Sorting and filtering work on the MODEL value, so they are unaffected by
         * the replica; a bulk write (fill handle, paste) bypasses the editor and
         * its option list entirely, which is worth policing once those are on.
         */
        prop: 'dataType',
        name: 'Tag Type',
        // wider than the text columns: the outlined field eats the 32px cell
        // gutter, a 14px left pad and a 24px chevron before any text shows
        size: 180,
        editor: tagTypeEditor,
        cellTemplate: createSelectCellTemplate(TAG_TYPE_OPTIONS),
      },
      { prop: 'displayName', name: 'Tag Display Name', size: 200 },
      {
        prop: 'dataTypeOfValues',
        name: 'Data Type',
        size: 140,
        editor: dataTypeOfValuesEditor,
        cellTemplate: createSelectCellTemplate(resolveDataTypeOptions),
      },
      {
        /**
         * Same dropdown mechanism as Tag Type, but the option list depends on
         * the row's own Tag Type, so it is resolved per row instead of fixed
         * — see `resolveRawUnitOptions`. Readonly (and greyed out by the cell
         * template) until a Tag Type is chosen, since there is nothing yet to
         * offer.
         *
         * Not yet handled: v1 clears a row's Raw Unit whenever its Tag Type
         * changes, since the old value may not be valid for the new list.
         * Nothing writes cell edits back to a shared row model yet for this
         * table, so that cascade has nowhere to hook in until it does.
         */
        prop: 'rawUnit',
        name: 'Unit',
        size: 180,
        editor: rawUnitEditor,
        cellTemplate: createSelectCellTemplate(resolveRawUnitOptions),
      },
    ],
  },
  {
    name: 'Advanced',
    children: [
      // { prop: 'displayAs', name: "Display As", size: 120, readonly: true },
      { prop: 'controllable', name: 'Controllable', size: 120, readonly: true },
      { prop: 'controller', name: 'Controller', size: 120, readonly: true },
      { prop: 'acceptableRangeValue', name: 'Acceptable Range/Value', size: 180, readonly: true },
      { prop: 'hidden', name: 'Hidden', size: 130, readonly: true },
    ],
  },
  /**
   * Not part of either group — a plain top-level column, so its header spans
   * the full header height instead of sitting under a subheader cell. Opens
   * `EditTagModalFormV2` for the row; not a real data field.
   */
  { prop: 'edit', name: '', size: 56, readonly: true, cellTemplate: editButtonCellTemplate },
];

/**
 * Asset Properties data sources — Raw Input Name, Tag Type, Tag Display
 * Name. No Raw Unit, no Advanced group: these rows have no
 * `advancedTagMapping` to show, so there's no edit modal either.
 * Visibility is set via the grid's Hide/Unhide selection toolbar, same as
 * every other tag table, not a per-row column.
 */
export const BASIC_TAG_TABLE_COLUMNS: ColumnData = [
  { prop: 'rawInputName', name: 'Raw Input Names', size: 400, readonly: true },
  {
    prop: 'dataType',
    name: 'Tag Type',
    size: 250,
    editor: tagTypeEditorForCalculated,
    cellTemplate: createSelectCellTemplate([{ label: 'Calculated', value: 'calculated' }]),
  },
  { prop: 'displayName', name: 'Tag Display Name', size: 400 },
  { prop: 'hidden', name: 'Hidden', size: 130, readonly: true },
];

/**
 * AI Flow (`streametric_ai`) data sources — same shape as
 * `RAW_INPUT_TAG_TABLE_COLUMNS`'s Regular group (Tag Type and Raw Unit both
 * live, dependent dropdowns) but with no Advanced group/edit modal, same
 * reasoning as `BASIC_TAG_TABLE_COLUMNS`.
 */
export const AI_FLOW_TAG_TABLE_COLUMNS: ColumnData = [
  { prop: 'rawInputName', name: 'Raw Input Names', size: 200, readonly: true },
  {
    prop: 'dataType',
    name: 'Tag Type',
    size: 180,
    editor: tagTypeEditor,
    cellTemplate: createSelectCellTemplate(TAG_TYPE_OPTIONS),
  },
  {
    prop: 'rawUnit',
    name: 'Raw Unit',
    size: 180,
    editor: rawUnitEditor,
    cellTemplate: createSelectCellTemplate(resolveRawUnitOptions),
  },
  { prop: 'displayName', name: 'Tag Display Name', size: 200 },
  { prop: 'hidden', name: 'Hidden', size: 130, readonly: true },
];

/**
 * Weather Alert API data sources — same header labels as AI Flow, but Tag
 * Type and Raw Unit stay fixed/read-only (a weather reading has one
 * pre-determined type and unit; v1's row component never made either
 * editable).
 */
export const WEATHER_TAG_TABLE_COLUMNS: ColumnData = [
  { prop: 'rawInputName', name: 'Raw Input Names', size: 200, readonly: true },
  { prop: 'dataType', name: 'Tag Type', size: 180, readonly: true },
  { prop: 'rawUnit', name: 'Raw Unit', size: 180, readonly: true },
  { prop: 'displayName', name: 'Tag Display Name', size: 200 },
  { prop: 'hidden', name: 'Hidden', size: 130, readonly: true },
];

export const DATA_SOURCE_TAG_TABLE_COLUMNS: Partial<Record<DataSourceTypeEnum, ColumnData>> = {
  [DataSourceTypeEnum.ASSET_PROPERTIES]: BASIC_TAG_TABLE_COLUMNS,
  [DataSourceTypeEnum.STREAMETRIC_AI]: AI_FLOW_TAG_TABLE_COLUMNS,
  [DataSourceTypeEnum.WEATHER_API]: WEATHER_TAG_TABLE_COLUMNS,
  /**
   * Integralert is a genuine 1:1 with the gateway table — same Regular +
   * Advanced groups, same edit modal (`EditTagModalFormV2`).
   */
  [DataSourceTypeEnum.INTEGRALERT]: RAW_INPUT_TAG_TABLE_COLUMNS,
};

/**
 * Columns for a Data Source's `dataSourceType`, or an empty list for a type
 * with no v2 table yet (e.g. `LOGS`, which has no UI surface in v1 either).
 */
export const getDataSourceTagTableColumns = (type?: DataSourceTypeEnum | null): ColumnData =>
  (type && DATA_SOURCE_TAG_TABLE_COLUMNS[type]) || [];

export const GATEWAY_TAG_TABLE_COLUMNS: Partial<Record<GatewayOptionsTypes, ColumnData>> = {
  [GatewayOptionsEnum.StreamLite]: RAW_INPUT_TAG_TABLE_COLUMNS,
  [GatewayOptionsEnum.StreamMLitePayload]: RAW_INPUT_TAG_TABLE_COLUMNS,
  [GatewayOptionsEnum.Inhand]: RAW_INPUT_TAG_TABLE_COLUMNS,
  [GatewayOptionsEnum.FX30]: RAW_INPUT_TAG_TABLE_COLUMNS,
  [GatewayOptionsEnum.Ewon]: AI_FLOW_TAG_TABLE_COLUMNS,
  [GatewayOptionsEnum.Kallipr]: AI_FLOW_TAG_TABLE_COLUMNS,
  /**
   * Still to come. EWON and Kallipr need a list WITHOUT the advanced-tag-mapping columns.
   */
};

/**
 * Columns for `gatewayType`, or an empty list for a type that has no v2 table
 * yet — deliberately not a guessed default, since the wrong head would render
 * columns the gateway has no data for.
 */
export const getGatewayTagTableColumns = (gatewayType?: GatewayOptionsTypes): ColumnData =>
  (gatewayType && GATEWAY_TAG_TABLE_COLUMNS[gatewayType]) || [];
